import type { ConferenceDay, GetConference } from "@/api_types/teacher";
import router from "@/router";
import FetchService from "@/services/fetch.service";
import { sort } from "@/utils/sort";
import { defineStore } from "pinia";
import { useAuthenticationStore } from "../authentication";
import { watch } from "vue";
import { useSchoolStore } from "../school";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";

// FetchService.parseResponse returns "" for a 204 with no content-type (real fetch
// Headers), and null when an application/json body is empty. A non-array payload
// (false, undefined, or an error object) fails that list only.
function conferenceList(value: unknown): { list: GetConference[]; failed: boolean } {
  if (Array.isArray(value)) return { list: value as GetConference[], failed: false };
  if (value === null || value === "") return { list: [], failed: false };
  return { list: [], failed: true };
}

export const useConferenceStore = defineStore("teacher/conference", {
  state: () => {
    return {
      selected: null as GetConference | null,
      selectedDay: null as ConferenceDay | null,
      conferences: [] as GetConference[],
      current_conferences: [] as GetConference[],
      archived_conferences: [] as GetConference[],
      error: null as string | null,
      loading: false,
      loadSeq: 0,
      tab: "schedule" as "agenda" | "schedule",
      loadingPromise: null as null | Promise<any>,
      resolveLoadingConf: (() => {}) as () => any
    };
  },
  getters: {
    getById: (state) => {
      return (conferenceId: number | string) =>
        state.conferences.find((conference) => conference.conference_id === conferenceId);
    }
  },
  actions: {
    async loadConferences() {
      const seq = ++this.loadSeq;
      this.loading = true;
      const authStore = useAuthenticationStore();
      if (
        (!useSchoolStore().school_id &&
          useAuthenticationStore().jwtData.authentication_type === "district") ||
        authStore.level === "parent" ||
        authStore.level == "support"
      ) {
        if (seq !== this.loadSeq) return;
        this.loading = false;
        this.resolveLoadingConf();
        return;
      }

      const stillCurrent = () => seq === this.loadSeq;
      try {
        const [current_conferences, archived_conferences] = await Promise.all([
          FetchService.fetchData("/conferences", "get").then((res) => {
            if (stillCurrent() && Array.isArray(res) && !this.conferences.length && res.length) {
              this.conferences = res;
              this.setCurrentConferences(res);
            }
            return res;
          }),
          FetchService.fetchData("/conferences?archived=true", "get")
        ]);

        if (!stillCurrent()) return;

        const current = conferenceList(current_conferences);
        const archived = conferenceList(archived_conferences);
        this.error = current.failed || archived.failed ? "load_failed" : null;
        this.conferences = [...current.list, ...archived.list];
        this.setCurrentConferences(current.list);
        this.archived_conferences = sort(archived.list, (i: any) => i.start, false);

        const query = router.currentRoute.value.query;
        if (query?.conference_id && this.conferences.length > 0) {
          const conf =
            this.conferences.find((c) => c.conference_id == +query.conference_id! && !c.team) ||
            this.conferences.find((c) => c.conference_id == +query.conference_id!);

          if (query?.team_uid) {
            const teamConf = this.conferences.find(
              (c) => c.team?.uid && c.team?.uid == query.team_uid
            );
            this.setSelected(
              teamConf ||
                conf ||
                (this.current_conferences.length > 0 ? this.current_conferences[0] : null)
            );
          } else {
            this.setSelected(
              conf || (this.current_conferences.length > 0 ? this.current_conferences[0] : null)
            );
          }
        } else if (this.current_conferences.length > 0) {
          this.setSelected(this.current_conferences[0]);
        }
      } catch (error) {
        if (!stillCurrent()) return;
        console.error("Error loading conferences:", error);
        this.error = "load_failed";
        this.conferences = [];
        this.current_conferences = [];
        this.archived_conferences = [];
      } finally {
        if (stillCurrent()) {
          this.loading = false;
          this.resolveLoadingConf();
        }
      }
    },
    setCurrentConferences(conferences) {
      this.current_conferences = sort(conferences, (i: any) => i.start, false);
    },
    setSelected(sel) {
      if (!sel) {
        this.selected = null;
        this.selectedDay = null;
        return;
      }

      this.selected = sel;
      this.selectedDay =
        sel.conference_days && sel.conference_days.length > 0 ? sel.conference_days[0] : null;

      if (!["school", "district"].includes(useAuthenticationStore().level))
        router.push({
          query: {
            ...(router.currentRoute.value.query || {}),
            conference_id: sel?.conference_id || undefined,
            team_uid: sel?.team?.uid || undefined
          }
        });
    },
    updatedConferenceDay(event_id, day_id, data: Partial<ConferenceDay>) {
      const conferenceDay = this.conferences
        .find((c) => c.conference_id == event_id)
        ?.conference_days.find((d) => d.conference_days_id === day_id);
      console.log(conferenceDay);

      if (conferenceDay) {
        Object.assign(conferenceDay, data);
        return true;
      }
      return false;
    },
    updatedConference(event_id, data: Partial<GetConference>) {
      const conference = this.conferences.find((c) => c.conference_id == event_id);
      console.log(conference);

      if (conference) {
        Object.assign(conference, data);
        return true;
      }
      return false;
    },
    getDayJs(datetime: Date | string) {
      dayjs.extend(utc);
      dayjs.extend(timezone);
      dayjs.extend(advancedFormat);
      // dayjs.tz.setDefault(useSchoolStore().school?.timezone);
      console.log(useSchoolStore().school?.timezone);
      console.log(dayjs().format());
      console.log(dayjs().tz(useSchoolStore().school?.timezone).format());
      // return dayjs(datetime).tz(useSchoolStore().school?.timezone);
      return dayjs(datetime);
    },
    async apiUpdateConference(event_id, data: Partial<GetConference>) {
      return FetchService.fetchData(`/conferences/${event_id}`, "put", data);
    },
    async apiUpdateConferenceDay(day_id, data: Partial<ConferenceDay>) {
      return FetchService.fetchData(`/conference-days/${day_id}`, "put", data);
    },
    async addConference(
      data: Pick<GetConference, "conference_name" | "open_tstamp" | "closed_tstamp">
    ) {
      const res = await FetchService.fetchData("/conferences", "post", data);
      if (res?.conference_id) {
        this.conferences.push(res);
        this.setCurrentConferences(this.current_conferences.concat(res));
      }
      return res;
    },
    async deleteConference(conference_id: number) {
      const res = await FetchService.fetchData("/conferences/" + conference_id, "delete");
      if (res?.success) {
        this.conferences = this.conferences.filter((c) => c.conference_id !== conference_id);
        this.current_conferences = this.current_conferences.filter(
          (c) => c.conference_id !== conference_id
        );
        this.archived_conferences = this.archived_conferences.filter(
          (c) => c.conference_id !== conference_id
        );
      }
      return res;
    },
    async load() {
      this.loadingPromise = new Promise((res) => {
        const pre = this.resolveLoadingConf;
        this.resolveLoadingConf = () => {
          res(true);
          pre();
        };
      });
      await this.loadConferences();
    }
  },
  onLoad() {
    this.loadingPromise = new Promise((res) => {
      const pre = this.resolveLoadingConf;
      this.resolveLoadingConf = () => {
        res(true);
        pre();
      };
    });
    const authStore = useAuthenticationStore();
    // An expired token must not count as the only fetch. Also watch level so a
    // teacher SSO login loads after the route moves off parent. loadSeq drops a
    // response from an older request.
    watch(
      () => [authStore.jwt?.access_token, authStore.level] as const,
      ([token, level], previous) => {
        const tokenChanged = !previous || previous[0] !== token;
        if (tokenChanged) {
          this.loadSeq += 1;
          this.conferences = [];
          this.current_conferences = [];
          this.archived_conferences = [];
          this.selected = null;
          this.selectedDay = null;
          this.error = null;
          this.loading = false;
        }
        if (token && level !== "parent" && level !== "support" && authStore.isLoggedIn()) {
          this.loadConferences();
        }
      },
      { immediate: true }
    );
  }
});
